type Theme = 'light' | 'dark' | 'auto';
type ResolvedTheme = Exclude<Theme, 'auto'>;

const toTheme = (raw: unknown, strict: boolean = false): Theme | null => {
    const value = (typeof raw === 'string' ? raw : String(raw)).trim().toLowerCase();

    return (
        value === 'light' ? 'light' :
            value === 'dark' ? 'dark' :
                value === 'auto' ? 'auto' :
                    strict ? null : 'auto'
    )
}

const isTheme = (raw: Theme): boolean => {
    const value = toTheme(raw);
    return value === 'light' || value === 'dark' || value === 'auto';
}

const isLightTheme = (raw: Theme): boolean => {
    const value = toTheme(raw);
    return value === 'light';
}

const isDarkTheme = (raw: Theme): boolean => {
    const value = toTheme(raw);
    return value === 'dark';
}

const isAutoTheme = (raw: Theme): boolean => {
    const value = toTheme(raw);
    return value === 'auto';
}

const getCurrentTheme = (): Theme => {
    const root = document.querySelector('html');
    return toTheme(root.getAttribute('data-theme'));
}

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/**
 * Returns the user's system theme preference.
 * Falls back to 'light' on SSR or when matchMedia is unsupported.
 */
const getSystemTheme = (): ResolvedTheme => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return 'light';
    }

    return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light';
};

/**
 * Resolves a theme value — if 'auto', returns the actual system preference.
 */
const resolveTheme = (theme: Theme): ResolvedTheme => theme === 'auto' ? getSystemTheme() : theme;

/**
 * Subscribe to system theme changes. Returns a cleanup function.
 */
const onSystemThemeChange = (
    callback: (theme: ResolvedTheme) => void
): (() => void) => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {
        };
    }

    const mediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
    const handler = (e: MediaQueryListEvent): void => {
        callback(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
};

export const AppTheme = {
    cast: toTheme,
    is: {
        theme: isTheme,
        light: isLightTheme,
        dark: isDarkTheme,
        auto: isAutoTheme
    },
    current: getCurrentTheme,
    get: getSystemTheme,
    resolve: resolveTheme,
    register: onSystemThemeChange,
}

export type {Theme, ResolvedTheme};

export default AppTheme;
